import { useEffect, useRef, type ReactElement } from 'react';
import { Text } from '@mantine/core';
import MegaGrid, { useMegaGridState, type MegaGridColumn, type MegaGridGroupByPill } from '@/design/MegaGrid/MegaGrid';
import { formatPercent, formatTimestamp, formatTokens, formatUsd } from '@/shared/analytics/MetricFormatting';
import type { InteractionRecord } from '@/shared/analytics/AnalyticsTypes';
import { scoreColor } from '@/design/ScoreColors';
import { onTopicEfficiency, sumCost, sumDriftTokens, sumTokens, type DriftTurn } from './DriftMetrics';

interface DriftSessionTopicGridProps {
    turns: DriftTurn[];
    /** Opens the interaction drawer for a clicked prompt-level (leaf) row. */
    onSelect: (record: InteractionRecord) => void;
    persistenceKey?: string;
}

/**
 * MegaGrid orders groups only by count or by group-key value, not by a rolled-up
 * metric. To surface the most-drifted sessions first we fold each session's
 * drift total into its group key as a zero-padded prefix, then sort the session
 * level by value descending. The label decodes back to the raw session id.
 */
const DRIFT_KEY_PAD = 12;

function sessionSortKey(turn: DriftTurn): string {
    return `${String(turn.sessionDriftTokens).padStart(DRIFT_KEY_PAD, '0')}|${turn.sessionId}`;
}

function humanSession(sortKey: string): string {
    const sessionId = sortKey.slice(sortKey.indexOf('|') + 1);
    return `Session ${sessionId.slice(0, 6)}`;
}

function groupEfficiency(rows: DriftTurn[]): number {
    return onTopicEfficiency(sumTokens(rows), sumDriftTokens(rows));
}

const GROUP_PILLS: MegaGridGroupByPill[] = [
    { columnKey: 'session', label: 'Session' },
    { columnKey: 'topic', label: 'Topic' },
];

const COLUMNS: MegaGridColumn<DriftTurn>[] = [
    {
        key: 'when',
        header: 'Turn',
        minWidth: 200,
        noGroup: true,
        sortValue: (r) => new Date(r.timestamp),
        render: (r) => (
            <Text size="xs" c="dimmed" ff="monospace">
                {formatTimestamp(r.timestamp)}
            </Text>
        ),
    },
    {
        key: 'session',
        header: 'Session',
        defaultHidden: true,
        noFilter: true,
        sortValue: (r) => r.sessionId,
        groupValue: sessionSortKey,
        groupLabel: (key) => humanSession(key),
        render: (r) => <Text size="xs">{humanSession(sessionSortKey(r))}</Text>,
    },
    {
        key: 'topic',
        header: 'Topic',
        defaultHidden: true,
        noFilter: true,
        sortValue: (r) => r.topic,
        groupValue: (r) => r.topic,
        render: (r) => <Text size="xs">{r.topic}</Text>,
    },
    {
        key: 'tokens',
        header: 'Session tokens',
        align: 'right',
        noGroup: true,
        sortValue: (r) => r.tokens,
        render: (r) => (
            <Text size="xs" ff="monospace">
                {formatTokens(r.tokens)}
            </Text>
        ),
        groupRender: (rows) => (
            <Text size="xs" ff="monospace" fw={600}>
                {formatTokens(sumTokens(rows))}
            </Text>
        ),
    },
    {
        key: 'driftTokens',
        header: 'Drift tokens',
        align: 'right',
        noGroup: true,
        sortValue: (r) => (r.isDrift ? r.tokens : 0),
        render: (r) =>
            r.isDrift ?
                <Text size="xs" ff="monospace">
                    {formatTokens(r.tokens)}
                </Text>
            :   <Text size="xs" c="dimmed">
                    —
                </Text>,
        groupRender: (rows) => (
            <Text size="xs" ff="monospace" fw={600}>
                {formatTokens(sumDriftTokens(rows))}
            </Text>
        ),
    },
    {
        key: 'cost',
        header: 'Cost',
        align: 'right',
        noFilter: true,
        noGroup: true,
        sortValue: (r) => r.costUsd,
        render: (r) => (
            <Text size="xs" ff="monospace">
                {formatUsd(r.costUsd)}
            </Text>
        ),
        groupRender: (rows) => (
            <Text size="xs" ff="monospace" fw={600}>
                {formatUsd(sumCost(rows))}
            </Text>
        ),
    },
    {
        key: 'efficiency',
        header: 'Efficiency',
        align: 'right',
        noGroup: true,
        noFilter: true,
        render: () => (
            <Text size="xs" c="dimmed">
                —
            </Text>
        ),
        groupRender: (rows): ReactElement => {
            const efficiency = groupEfficiency(rows);
            return (
                <Text size="xs" ff="monospace" fw={600} style={{ color: scoreColor(efficiency * 100) }}>
                    {formatPercent(efficiency)}
                </Text>
            );
        },
    },
];

/**
 * A person's drifted activity, grouped by session then topic and ordered so the
 * heaviest-drift sessions lead. Group headers roll up session/topic tokens, drift
 * tokens, and on-topic efficiency aligned under each column.
 */
export default function DriftSessionTopicGrid({ turns, onSelect, persistenceKey }: DriftSessionTopicGridProps): ReactElement {
    const controller = useMegaGridState({
        columns: COLUMNS,
        persistenceKey,
        defaultDensity: 'comfortable',
        defaultGroupBy: ['session', 'topic'],
        allowMultiSort: true,
    });

    const seeded = useRef(false);
    useEffect(() => {
        if (seeded.current) return;
        seeded.current = true;
        controller.setGroupSort('session', 'value', 'desc');
    }, [controller]);

    return (
        <MegaGrid
            columns={COLUMNS}
            rows={turns}
            controller={controller}
            getRowKey={(r) => r.id}
            onRowClick={(r) => onSelect(r.record)}
            groupByPills={GROUP_PILLS}
            searchAccessor={(r) => `${r.topic} ${humanSession(sessionSortKey(r))}`}
            searchPlaceholder="Search sessions or topics"
            striped
            minWidth={720}
            enableExport
            exportName="session-drift-detail"
        />
    );
}
